<template>
  <d-modal
    :modal="modal"
    :title="
      editMode
        ? $t('project-apis.edit.page-heading')
        : $t('project-apis.add.page-heading')
    "
    @submitModal="handleSubmitModal"
    @closeModal="handleCloseModal"
    :isSubmitting="isSubmitting"
    maxWidth="800px"
  >
    <template slot="body">
      <d-loading class="mb-4" v-if="loading.page"></d-loading>
      <v-row class="mb-1" v-else>
        <v-col cols="12" md="6" class="mb-3">
          <v-text-field
            :label="$t('project-apis.add.form.name')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.name"
            :rules="rule.required"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6" class="mb-3">
          <v-text-field
            :label="$t('project-apis.add.form.slug')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.slug"
            :rules="rule.slug"
            :error="errorMessages.slug.length > 0"
            :error-messages="errorMessages.slug"
            @blur="checkSlugIsValid"
            :loading="isCheckingSlug"
            @keyup="lowerCaseSlug"
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="12" class="mb-3">
          <v-textarea
            auto-grow
            :label="$t('project-apis.add.form.description')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.description"
            :rules="rule.required"
            rows="2"
          ></v-textarea>
        </v-col>

        <v-col cols="12" md="3" class="mb-3">
          <v-select
            filled
            hide-details="auto"
            :items="httpMethods"
            item-text="displayText"
            item-value="value"
            clearable
            :label="$t('project-apis.add.form.method')"
            v-model="form.httpMethodCode"
            :rules="rule.required"
          ></v-select>
        </v-col>

        <v-col cols="12" md="9" class="mb-3">
          <v-text-field
            :placeholder="$t('project-apis.add.form.api-url')"
            hide-details="auto"
            filled
            type="text"
            v-model="form.apiUrl"
            :rules="rule.required"
          >
            <template v-slot:prepend-inner>
              <span>{{ projectBaseUrl }}/</span>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12">
          <v-tabs v-model="tab">
            <v-tab id="tab-1">{{
              $t("project-apis.add.form.headers.title")
            }}</v-tab>
            <v-tab id="tab-2">{{
              $t("project-apis.add.form.parameters.title")
            }}</v-tab>
            <v-tab id="tab-3">{{
              $t("project-apis.add.form.request-body.title")
            }}</v-tab>
            <v-tab id="tab-4">{{
              $t("project-apis.add.form.responses.title")
            }}</v-tab>
          </v-tabs>

          <div class="pt-4">
            <v-tabs-items v-model="tab">
              <v-tab-item href="tab-1">
                <v-row v-for="(header, index) in form.headers" :key="index">
                  <v-col cols="6" class="mb-3">
                    <div class="d-flex align-center">
                      <v-btn
                        size="36"
                        depressed
                        icon
                        class="mt-2 mr-2"
                        @click="removeHeader(header)"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                      <v-text-field
                        :label="$t('project-apis.add.form.headers.key')"
                        filled
                        type="text"
                        v-model="form.headers[index].key"
                        hide-details
                        :rules="rule.required"
                      ></v-text-field>
                    </div>
                  </v-col>
                  <v-col cols="6" class="mb-3">
                    <v-textarea
                      auto-grow
                      :label="$t('project-apis.add.form.headers.description')"
                      filled
                      v-model="form.headers[index].description"
                      rows="1"
                      hide-details
                      :rules="rule.required"
                    ></v-textarea>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="mb-3">
                    <v-btn color="gray darken-2" outlined @click="addHeader">
                      <v-icon left> mdi-plus </v-icon>
                      <span class="subtitle-2 text-none">{{
                        $t("defaults.button.create")
                      }}</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item href="tab-2">
                <v-row
                  v-for="(parameter, index) in form.parameters"
                  :key="index"
                >
                  <v-col cols="6" class="mb-3">
                    <div class="d-flex align-center">
                      <v-btn
                        size="36"
                        depressed
                        icon
                        class="mt-2 mr-2"
                        @click="removeParameter(parameter)"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                      <v-text-field
                        :label="$t('project-apis.add.form.parameters.key')"
                        filled
                        type="text"
                        v-model="form.parameters[index].key"
                        hide-details
                        :rules="rule.required"
                      ></v-text-field>
                    </div>
                  </v-col>
                  <v-col cols="6" class="mb-3">
                    <v-textarea
                      auto-grow
                      :label="
                        $t('project-apis.add.form.parameters.description')
                      "
                      filled
                      v-model="form.parameters[index].description"
                      rows="1"
                      hide-details
                      :rules="rule.required"
                    ></v-textarea>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="mb-3">
                    <v-btn color="gray darken-2" outlined @click="addParameter">
                      <v-icon left> mdi-plus </v-icon>
                      <span class="subtitle-2 text-none">{{
                        $t("defaults.button.create")
                      }}</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item href="tab-3">
                <v-row>
                  <v-col class="mb-3">
                    <BaseJsonEditor
                      :content="form.requestBody"
                      mode="text"
                      :mainMenuBar="false"
                      :navigationBar="false"
                      :statusBar="false"
                      :onRenderValue="
                        (updatedContent) => {
                          form.requestBody = updatedContent;
                        }
                      "
                      :onChange="
                        (updatedContent) => {
                          form.requestBody = updatedContent;
                        }
                      "
                    ></BaseJsonEditor>
                  </v-col>
                </v-row>
              </v-tab-item>
              <v-tab-item href="tab-4">
                <v-row v-for="(response, index) in form.responses" :key="index">
                  <v-col cols="4" class="mb-3">
                    <div class="d-flex align-center">
                      <v-btn
                        size="36"
                        depressed
                        icon
                        class="mt-2 mr-2"
                        @click="removeResponse(response)"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                      <v-text-field
                        :label="$t('project-apis.add.form.responses.code')"
                        filled
                        v-model="form.responses[index].code"
                        hide-details
                        :rules="rule.required"
                      ></v-text-field>
                    </div>
                  </v-col>
                  <v-col cols="8" class="mb-3">
                    <BaseJsonEditor
                      :content="form.responses[index].description"
                      mode="text"
                      :mainMenuBar="false"
                      :navigationBar="false"
                      :statusBar="false"
                      :onRenderValue="
                        (updatedContent) => {
                          form.responses[index].description = updatedContent;
                        }
                      "
                      :onChange="
                        (updatedContent) => {
                          form.responses[index].description = updatedContent;
                        }
                      "
                    ></BaseJsonEditor>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="mb-3">
                    <v-btn color="gray darken-2" outlined @click="addResponse">
                      <v-icon left> mdi-plus </v-icon>
                      <span class="subtitle-2 text-none">{{
                        $t("defaults.button.create")
                      }}</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </v-col>
      </v-row>
    </template>
  </d-modal>
</template>
  
  <script>
import {
  checkApiSlugAvailability,
  addProjectApi,
  getProjectApiById,
  updateProjectApi,
} from "@/services/project";

import BaseJsonEditor from "@/components/BaseJsonEditor.vue";

const initialForm = {
  id: null,
  name: null,
  slug: null,
  description: null,
  httpMethodCode: null,
  apiUrl: null,
  headers: [
    {
      key: null,
      description: null,
    },
  ],
  parameters: [
    {
      key: null,
      description: null,
    },
  ],
  requestBody: undefined,
  responses: [
    {
      code: null,
      description: undefined,
    },
  ],
};

const initialErrorMessages = {
  slug: [],
};

export default {
  setup() {},
  components: { BaseJsonEditor },
  props: {
    modal: {
      type: Boolean,
      default: false,
    },
    item: Object,
    projectBaseUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      tab: "tab-1",
      isSubmitting: false,
      form: { ...initialForm },
      rule: {
        slug: [
          (v) => !!v || this.$t("defaults.rules.required"),
          (v) =>
            /^[a-z0-9-]{1,25}$/.test(v?.toLowerCase()) ||
            this.$t("project-apis.add.rules.slug-invalid"),
        ],
      },
      httpMethods: [],
      errorMessages: { ...initialErrorMessages },
      isCheckingSlug: false,
      loading: {
        page: false,
      },
    };
  },
  computed: {
    editMode() {
      return !!this.item?.id;
    },
  },
  methods: {
    lowerCaseSlug() {
      this.form.slug = this.form?.slug?.toLowerCase();
    },
    async handleSubmitModal() {
      const responses = this.form.responses.map((item) => {
        return {
          code: item.code,
          description: item.description ? item.description.text : undefined,
        };
      });

      const form = {
        ...this.form,
        requestBody: this.form.requestBody
          ? this.form.requestBody.text
          : undefined,
        responses: responses,
      };

      if (this.editMode) {
        await this.submitUpdateProjectApi(form);
        return;
      }
      await this.submitAddProjectApi(form);
    },
    async submitAddProjectApi(form) {
      try {
        this.isSubmitting = true;
        await addProjectApi(form);

        this.$eventBus.$emit("notification", {
          type: "success",
          message: this.$t("project-apis.add.alert.added"),
        });
        this.$emit("submitModal");
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    async submitUpdateProjectApi(form) {
      try {
        this.isSubmitting = true;
        await updateProjectApi(form);

        this.$eventBus.$emit("notification", {
          type: "success",
          message: this.$t("project-apis.edit.alert.updated"),
        });
        this.$emit("submitModal");
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    handleCloseModal() {
      this.$emit("closeModal");
    },
    async checkSlugIsValid() {
      const { slug, id } = this.form;
      if (!slug) return;

      if (/^[a-z0-9-]{1,25}$/.test(slug?.toLowerCase())) {
        try {
          this.isCheckingSlug = true;
          const response = await checkApiSlugAvailability({
            id: this.$route.params.id,
            slug,
            apiId: this.editMode ? id : null,
          });
          if (!response) {
            this.errorMessages.slug = [
              this.$t("project-apis.add.rules.slug-is-taken"),
            ];
            return;
          }
          this.errorMessages.slug = [];
        } finally {
          this.isCheckingSlug = false;
        }
      }
    },
    async getProjectApiById() {
      try {
        const { data, items } = await getProjectApiById({
          projectId: this.$route.params.id,
          id: this.item?.id || -1,
        });
        this.httpMethods = items.httpMethods;

        const responses = data.responses.map((item) => {
          return {
            code: item.code,
            description:
              item.description != undefined
                ? { text: item.description }
                : undefined,
          };
        });

        this.form = {
          ...data,
          id: data.id ? data.id : this.item?.id || -1,
          projectId: this.$route.params.id,
          requestBody:
            data.requestBody != undefined
              ? { text: data.requestBody }
              : undefined,
          responses: responses,
        };
      } catch {
        this.$eventBus.$emit("notification", {
          type: "error",
          message: this.$t("defaults.text.request-error"),
        });
      }
    },
    addHeader() {
      this.form.headers.push({
        key: null,
        description: null,
      });
    },
    removeHeader(value) {
      let index = this.form.headers.indexOf(value);

      if (index > -1) {
        this.form.headers.splice(index, 1);
      }
    },
    addParameter() {
      this.form.parameters.push({
        key: null,
        description: null,
      });
    },
    removeParameter(value) {
      let index = this.form.parameters.indexOf(value);

      if (index > -1) {
        this.form.parameters.splice(index, 1);
      }
    },
    addResponse() {
      this.form.responses.push({
        code: null,
        description: undefined,
      });
    },
    removeResponse(value) {
      let index = this.form.responses.indexOf(value);

      if (index > -1) {
        this.form.responses.splice(index, 1);
      }
    },
  },
  async mounted() {
    try {
      this.loading.page = true;
      await this.getProjectApiById();
    } finally {
      this.loading.page = false;
    }
  },
};
</script>
  